/**
 * The Charter career bridge (M5 plan C16; spec §7.3, §3.3 S-M2-12, S-M2-13, S-M3-12, S-M7-1, §12.6): the M2 Career
 * and the strategic worker agree on requests, standings, the story tier and relink sponsors.
 *
 * - **Faction requests as contracts (S-M2-12):** the open `FactionRequest`s of the watched system
 *   (`SystemEconomyView.requests`) become offers on the issuing faction's station boards through M2's generator seam
 *   (`bindExtraOffers`). A request names its station, or none (then every contracts station of that faction in the
 *   system shows it). `deliver` → a `delivery` of the ware (tonnes capped to M2's delivery range, reward scaled with
 *   them); `clearRaiders` → a `clearRaiders` with a seeded marker and the tier the reward's place in M2's range gives.
 *   The reward is the worker's (premium ×`unlockMult` at standing ≥ `unlockStanding`, the M2 rule); the standing is
 *   M2's `contractStanding` (the faction's flat override below its `until`, D-335 D4, else the seeded range). Offer
 *   ids are `${station}:${cycle}:${requestId}`, so completing one is recognised by its id and a request done once is
 *   never offered again this session or while its id is kept.
 *   **Works requests (M8 SNK-6b2):** a request with a `siteId` is checked first and becomes a goods `delivery` over
 *   non-raw `cargo.parts` (`params.good`, `params.siteId`): tonnes = the site's missing tonnes held to the hold, no
 *   `unlockMult`, the reward only the board's estimate; the hand-in is paid what the worker's `worksDeliver` paid
 *   (`career/runtime.ts`). A works request for a raw ware is never offered, so never a plain delivery.
 *   `zoneSupport` requests are not offered yet (they need an M2 contract type; the gate's list).
 * - **Completion reports:** a request contract that reaches `completedIds` sends `playerContractDone` (the worker adds
 *   influence, §5.1); the standing comes from M2's own hand-in (§12.6: +5..+10). A works contract (`params.siteId`)
 *   is reported by the career runtime's hand-in with the treasury's actual `paid` (the accepted reward is only the
 *   board's estimate), so here it only marks its request done.
 * - **Standings mirror (S-M2-13):** the Career stays the source of truth. Its standings are sent as
 *   `playerStandings` on the first sync and whenever they change; the worker's deltas (command results, and
 *   `AdvanceResult.standingDeltas` when the client forwards them) are applied once through the Career's write path
 *   (`applyStandingDeltas`), after which the mirror follows by the same change detection.
 * - **Reconciliation (D-335 D4):** `reconciliationOffer` / `reconcile` run `reconciliation.ts` on the Career (the
 *   Charter HUD offers it over comms on the faction's docking refusal).
 * - **Guardian kill log (M7 §12.1):** `AdvanceResult.guardianKills` (the feature forwards them) are appended to the
 *   Career's bounded `guardianKills` log (`career/logs.ts`).
 * - **Story tier (S-M7-1):** `career.storyTier` (absent = 0) is sent as `setStoryTier` on load and on change.
 * - **Story re-apply (M7 S26):** once the Career has story state (a claimed tier or a `story` block), the story
 *   bridge's re-apply list (`src/story/bridge.ts createStorySync`, which also carries the tier) replaces the tier send:
 *   the whole list on load and on a fresh Reach (`reapplyStory`), and the changed commands on every story change.
 * - **Worker re-init (M7 reapply):** the strategic link re-initializes the worker on every career enter (a load with
 *   or without its strategic companion, a fresh Reach, Continue) and then calls `workerReinit`: the shared sync's
 *   sent-set is cleared and the whole re-apply list sent once (this bridge is its single owner; the story service's
 *   `reapply` is not called too), and the standings mirror is re-sent on the next step, so a fresh worker matches the
 *   Career. Idempotent: the re-apply list sets, never adds.
 * - **Relink sponsor (S-M3-12):** M3's `REV.sponsorChosen` (relay index, faction in `FACTION_KEYS` order) is
 *   remembered; the relay's `REV.rebootEnd` with 0 (online) sends `relinkSponsored { relayId, factionId }`.
 *
 * `step()` runs once per frame: it compares the career reference (no work while it is unchanged) and reads new Reach
 * events; it allocates only when something changed. Commands are fire-and-forget; a refusal is logged.
 */
import type { CareerAccess } from '../bridge/m2/career'
import { m2IdOfSite } from '../bridge/m2/stations'
import type { SystemLayout } from '../galaxy/layout/types'
import { cargoClassOf, holdMultOf } from '../career/hullFit'
import { STRATEGIC_GOOD_IDS, type StrategicGoodId } from '../career/parts'
import { applyStandingDeltas as applyCareerDeltas, type CareerStandingDelta } from '../career/standing'
import type { Career } from '../career/types'
import { eventSlot, firstReadable, type EventRing } from '../combat/events'
import { bindExtraOffers, contractStanding, pickMarker, type ContractOffer } from '../contracts/generate'
import { logger } from '../core/logger'
import { stationDef, stationIndexOf } from '../data/m2'
import { SIM_FACTION_IDS, WARE_IDS, type M2Data, type SimFactionId, type StationDef, type WareId } from '../data/m2Schemas'
import { FACTION_KEYS } from '../galaxy/layout/config'
import { REV } from '../reach/events'
import { hashString, seededUnit } from '../space/hash'
import type { CommandResult, FactionId, FactionRequest, FactionRequestView, GuardianKill, StandingDelta, StrategicCommand, SystemEconomyView } from '../strategic/types'
import { withGuardianKills } from '../career/logs'
import { fail, type CmdResult } from '../career/result'
import { createStorySync, wantsStoryReapply, type StorySync, type StorySyncContext } from '../story/bridge'
import type { StoryView } from '../strategic/types'
import { withResonanceMeter } from '../story-link/missions/m2'
import { acceptReconciliation, reconciliationOffer, type ReconciliationOffer } from './reconciliation'

/** Salts of the per-request draws. */
const SALT_STANDING = 1

/** A system view that may carry the system's open requests (M5 S-M2-12). */
export type RequestView = SystemEconomyView & { readonly requests?: readonly FactionRequest[] }

/** What the bridge needs of the strategic client (`StrategicClient` satisfies it). */
export interface CharterClientPort {
  command(cmd: StrategicCommand): Promise<CommandResult>
  readonly systemView: RequestView | null
}

/** What the bridge reads of the Reach (the runtime's core satisfies it). */
export interface CharterReachPort {
  readonly galaxy: { readonly relays: readonly { readonly id: string }[] }
  readonly events: EventRing
}

export interface CareerBridgeDeps {
  /** The Career's read and write path; null before a career runs. */
  access: () => CareerAccess | null
  client: CharterClientPort
  /** M2's live data (its `stations` follow the entered system). */
  data: M2Data
  /** Absent: no relink sponsorship (no Reach). */
  reach?: CharterReachPort
  /** M7 S26: the story re-apply sender (shared with the story bridge so neither repeats the other); default: own. */
  storySync?: StorySync
  /** M7 S26: the world the re-apply reads (the player's system, POI → system). */
  storyWorld?: () => StorySyncContext
  /** R3-1: the live system layout (request site ids → board ids at the home cluster); absent: slugs only. */
  layout?: () => RequestLayout
}

export interface CareerBridge {
  /** Binds the request offers into M2's generator. */
  install(): void
  /** Per frame: the mirror, the story tier, completions and relink events (no allocation while nothing changed). */
  step(): void
  /** Applies worker standing deltas to the Career once (S-M2-13). */
  applyStandingDeltas(deltas: readonly StandingDelta[]): void
  /** Sends a strategic command and applies the standing deltas its result carries. */
  command(cmd: StrategicCommand): Promise<CommandResult>
  /** Sets the Career's story tier (dev toggle, M7); `step` sends it. */
  setStoryTier(tier: number): void
  /** Fits or removes the resonance meter on the flown ship (dev toggle, M7 S37) through the Career's write path; false when no career runs or the write is refused. */
  setResonanceMeter(fitted: boolean): boolean
  /**
   * M7 S26: sends the whole story re-apply list now (load, a fresh Reach). With the worker's view (a fresh Reach)
   * the story's online days are rebased to the worker's counter first.
   */
  reapplyStory(view?: Pick<StoryView, 'onlineDay'>): void
  /**
   * M7: the worker was re-initialized (`strategic-link` `onWorkerReinit`): the story re-apply now (`reapplyStory`)
   * and the standings mirror on the next step, whatever the previous timeline sent.
   */
  workerReinit(view?: Pick<StoryView, 'onlineDay'>): void
  /** Appends the worker's guardian kills to the Career's log (M7 §12.1 logging). */
  recordGuardianKills(kills: readonly GuardianKill[]): void
  /** The open comms-only reconciliation offer (D-335 D4, `reconciliation.ts`), or null. */
  reconciliationOffer(): ReconciliationOffer | null
  /** Accepts the reconciliation offer through the Career's write path (pays, restores the standing). */
  reconcile(): CmdResult<Career>
  dispose(): void
}

// ---------------------------------------------------------------------------------------------
// Pure mapping
// ---------------------------------------------------------------------------------------------

/** The M2 faction of a strategic faction (the hidden guardian cell has none). */
export function simFactionOf(id: FactionId): SimFactionId | null {
  return (SIM_FACTION_IDS as readonly string[]).includes(id) ? (id as SimFactionId) : null
}

/** The strategic faction of an M2 faction (every M2 faction has one). */
export function strategicFactionOf(id: SimFactionId): FactionId {
  return id
}

/** The Career's story tier (absent = 0: saves from before M5). */
export function careerStoryTier(career: Career): number {
  return career.storyTier ?? 0
}

/** The Career with story tier `tier` (an integer clamped to 0..5). */
export function withStoryTier(career: Career, tier: number): Career {
  const t = Number.isFinite(tier) ? Math.min(5, Math.max(0, Math.round(tier))) : 0
  return { ...career, storyTier: t }
}

/** The request id of a request contract id (`${station}:${cycle}:${requestId}`), or null. */
export function requestIdOf(contractId: string): string | null {
  const i = contractId.lastIndexOf(':')
  const key = i < 0 ? contractId : contractId.slice(i + 1)
  return key.startsWith('req-') ? key : null
}

/** A system's station sites, as `m2IdOfSite` reads them (null: not known). */
export type RequestLayout = Pick<SystemLayout, 'systemId' | 'stations' | 'home'> | null

/**
 * True when `req` belongs on `station`'s board in system `systemId`. R3-1: a request pinned to a site maps to its
 * board id through `m2IdOfSite` with `layout` (the home cluster's sites are `hub`, `refinery`, `scrapyard`, not slugs);
 * a layout of another system is ignored.
 */
export function requestFits(req: FactionRequest, station: StationDef, systemId: string, layout: RequestLayout = null): boolean {
  if (req.systemId !== systemId) return false
  if (simFactionOf(req.faction) !== station.faction) return false
  if (!station.services.includes('contracts')) return false
  if (req.stationId === null || req.stationId === station.id) return true
  return m2IdOfSite(req.stationId, layout !== null && layout.systemId === systemId ? layout : null) === station.id
}

function isM2Ware(w: string): w is WareId {
  return (WARE_IDS as readonly string[]).includes(w)
}

function isStrategicGood(w: string): w is StrategicGoodId {
  return (STRATEGIC_GOOD_IDS as readonly string[]).includes(w)
}

/**
 * The works delivery offer of works request `req` (M8 SNK-6b2, D-TBD T31): a `delivery` of a non-raw good from
 * `cargo.parts` into site `req.siteId`. Its tonnes are the site's missing tonnes (whole, at least 1), held to what the
 * hold can carry, never to M2's delivery range; its reward is the row's treasury price (premium × unit × tonnes) and
 * never takes `unlockMult`. That is only the board's estimate: the hand-in is paid what the worker prices
 * (`worksDeliver`). Null for a raw ware (or tetherstone), a request that isn't `deliver` or a row without a price.
 */
function worksOffer(career: Career, deadlineCycles: number, base: Omit<ContractOffer, 'type' | 'reward' | 'durationSeconds' | 'params'>, req: FactionRequestView): ContractOffer | null {
  if (req.kind !== 'deliver' || req.ware === undefined || !isStrategicGood(req.ware) || req.siteId === undefined) return null
  const qty = req.qty ?? 0
  const unit = req.unit ?? 0
  // M10 P5a6b H3: the tonnes of this good the hold carries on the flown hull (× its class multiplier; 1× today).
  const capacity = Math.floor(career.cargo.capacityT * holdMultOf(career.ship.defId, cargoClassOf(req.ware)))
  if (!(qty > 0) || !(unit > 0) || !(capacity >= 1)) return null
  const premium = req.premium !== undefined && req.premium > 0 ? req.premium : req.reward / (qty * unit)
  if (!(premium > 0)) return null
  const tons = Math.max(1, Math.min(Math.floor(qty), capacity))
  return {
    ...base,
    type: 'delivery',
    reward: Math.round(premium * unit * tons),
    durationSeconds: deadlineCycles * career.lease.cycleSeconds,
    params: { good: req.ware, tons, systemId: req.systemId, siteId: req.siteId },
  }
}

/** The M2 offer of request `req` on `station`'s board for lease cycle `cycle`, or null when M2 cannot carry it. */
export function requestOffer(data: M2Data, career: Career, station: StationDef, cycle: number, req: FactionRequest): ContractOffer | null {
  const cfg = data.contracts
  const types = cfg.types
  const seed = hashString(req.id)
  const premium = career.standings[station.faction] >= cfg.unlockStanding
  const mult = premium ? cfg.unlockMult : 1
  const base = {
    id: `${station.id}:${cycle}:${req.id}`,
    station: station.id,
    cycle,
    standing: contractStanding(cfg, station.faction, career.standings[station.faction], seededUnit(seed, 0, SALT_STANDING)),
    premium,
  }
  // M8 works request (SNK-6b2): checked first, so a works request never becomes a plain (raw) delivery.
  if (req.siteId !== undefined) return worksOffer(career, types.delivery.deadlineCycles, { ...base, premium: false }, req)
  switch (req.kind) {
    case 'deliver': {
      if (req.ware === undefined || !isM2Ware(req.ware) || req.ware === 'tetherstone') return null
      const qty = req.qty ?? 0
      if (!(qty > 0) || !(req.reward > 0)) return null
      const tons = Math.max(1, Math.min(Math.floor(qty), Math.floor(types.delivery.tons[1])))
      return {
        ...base,
        type: 'delivery',
        reward: Math.round(((req.reward * tons) / qty) * mult),
        durationSeconds: types.delivery.deadlineCycles * career.lease.cycleSeconds,
        params: { ware: req.ware, tons, systemId: req.systemId },
      }
    }
    case 'clearRaiders': {
      const tiers = data.encounters.contractTiers.length
      if (tiers === 0 || !(req.reward > 0)) return null
      const marker = pickMarker(data, station, types.clearRaiders.markerDistance, seed, 0)
      if (marker === null) return null
      const [lo, hi] = types.clearRaiders.reward
      const u = hi > lo ? (req.reward - lo) / (hi - lo) : 0
      const tier = Math.min(tiers - 1, Math.max(0, Math.floor(u * tiers)))
      return {
        ...base,
        type: 'clearRaiders',
        reward: Math.round(req.reward * mult),
        durationSeconds: types.clearRaiders.deadlineCycles * career.lease.cycleSeconds,
        params: { marker, tier, systemId: req.systemId },
      }
    }
    case 'zoneSupport':
      return null
  }
}

/** True when the career has accepted, completed or failed any contract of request `requestId`. */
function requestTaken(career: Career, requestId: string): boolean {
  const c = career.contracts
  const suffix = `:${requestId}`
  return c.active.some((a) => a.id.endsWith(suffix)) || c.completedIds.some((id) => id.endsWith(suffix)) || c.failedIds.some((id) => id.endsWith(suffix))
}

/** The request offers of `station` from the open requests of system `systemId`. */
export function requestOffers(
  data: M2Data,
  career: Career,
  station: StationDef,
  cycle: number,
  systemId: string,
  requests: readonly FactionRequest[],
  done: ReadonlySet<string> = new Set(),
  layout: RequestLayout = null,
): ContractOffer[] {
  const out: ContractOffer[] = []
  for (const req of requests) {
    if (done.has(req.id) || !requestFits(req, station, systemId, layout) || requestTaken(career, req.id)) continue
    const offer = requestOffer(data, career, station, cycle, req)
    if (offer !== null) out.push(offer)
  }
  return out
}

/** Worker deltas as Career deltas (hidden factions dropped; a `floor` rides along). */
export function careerDeltas(deltas: readonly StandingDelta[]): CareerStandingDelta[] {
  const out: CareerStandingDelta[] = []
  for (const d of deltas) {
    const faction = simFactionOf(d.factionId)
    if (faction === null || !Number.isFinite(d.delta) || d.delta === 0) continue
    out.push(d.floor !== undefined && Number.isFinite(d.floor) ? { faction, delta: d.delta, floor: d.floor } : { faction, delta: d.delta })
  }
  return out
}

/** The Career's standings in the worker's terms (`playerStandings`). */
export function mirrorStandings(career: Career): Partial<Record<FactionId, number>> {
  const out: Partial<Record<FactionId, number>> = {}
  for (const f of SIM_FACTION_IDS) out[strategicFactionOf(f)] = career.standings[f]
  return out
}

function sameStandings(a: Career['standings'] | null, b: Career['standings']): boolean {
  if (a === null) return false
  for (const f of SIM_FACTION_IDS) if (a[f] !== b[f]) return false
  return true
}

// ---------------------------------------------------------------------------------------------
// The bridge
// ---------------------------------------------------------------------------------------------

interface Tracked {
  faction: FactionId
  systemId: string
  reward: number
  /** A works contract: its `playerContractDone` (with the actual paid) is sent by the career runtime's hand-in. */
  works: boolean
}

export function createCareerBridge(deps: CareerBridgeDeps): CareerBridge {
  const { client, data } = deps
  const done = new Set<string>()
  const tracked = new Map<string, Tracked>()
  const sponsors = new Map<number, FactionId>()
  let lastCareer: Career | null = null
  let sentStandings: Career['standings'] | null = null
  let sentTier = -1
  let sentStory: Career['story'] | null = null
  const storySync = deps.storySync ?? createStorySync()
  let cursor = deps.reach?.events.head ?? 0
  let installed = false

  function send(cmd: StrategicCommand): void {
    void client.command(cmd).then(
      (r) => {
        if (!r.ok) logger.warn(`charter career bridge: ${cmd.type} refused (${r.error})`)
        else if (r.standingDeltas !== undefined && r.standingDeltas.length > 0) applyDeltas(r.standingDeltas)
      },
      (err: unknown) => logger.warn(`charter career bridge: ${cmd.type} failed`, err),
    )
  }

  function applyDeltas(deltas: readonly StandingDelta[]): void {
    const rows = careerDeltas(deltas)
    if (rows.length === 0) return
    const access = deps.access()
    if (access === null) return
    const out = access.apply('charterStandings', (c) => applyCareerDeltas(c, rows).value)
    if (!out.ok) logger.warn('charter career bridge: standing deltas not applied', out.error)
  }

  function offers(d: M2Data, career: Career, station: StationDef, cycle: number): readonly ContractOffer[] {
    const view = client.systemView
    const reqs = view?.requests
    if (view === null || reqs === undefined || reqs.length === 0) return []
    return requestOffers(d, career, station, cycle, view.systemId, reqs, done, deps.layout?.() ?? null)
  }

  /** Request contracts: track accepted ones, report completed ones. */
  function syncContracts(career: Career): void {
    const c = career.contracts
    for (const a of c.active) {
      if (tracked.has(a.id)) continue
      const reqId = requestIdOf(a.id)
      if (reqId === null || stationIndexOf(data, a.station) < 0) continue
      const systemId = a.params.systemId
      if (systemId === undefined) continue
      tracked.set(a.id, { faction: strategicFactionOf(stationDef(data, a.station).faction), systemId, reward: a.reward, works: a.params.siteId !== undefined })
    }
    for (const [id, t] of tracked) {
      if (c.completedIds.includes(id)) {
        tracked.delete(id)
        const reqId = requestIdOf(id)
        if (reqId !== null) done.add(reqId)
        if (!t.works) send({ type: 'playerContractDone', factionId: t.faction, systemId: t.systemId, reward: t.reward })
      } else if (c.failedIds.includes(id) || !c.active.some((a) => a.id === id)) {
        tracked.delete(id)
      }
    }
  }

  function syncCareer(career: Career): void {
    if (!sameStandings(sentStandings, career.standings)) {
      sentStandings = career.standings
      send({ type: 'playerStandings', standings: mirrorStandings(career) })
    }
    const tier = careerStoryTier(career)
    if (wantsStoryReapply(career)) {
      if (tier !== sentTier || career.story !== sentStory) {
        sentTier = tier
        sentStory = career.story
        for (const cmd of storySync.commands(career, deps.storyWorld?.())) send(cmd)
      }
    } else if (tier !== sentTier) {
      sentTier = tier
      send({ type: 'setStoryTier', tier })
    }
    syncContracts(career)
  }

  function readReach(reach: CharterReachPort): void {
    const ring = reach.events
    const head = ring.head
    for (let i = firstReadable(ring, cursor); i < head; i++) {
      const s = eventSlot(i)
      const type = ring.type[s]
      if (type === REV.sponsorChosen) {
        const key = FACTION_KEYS[ring.other[s]] as SimFactionId | undefined
        if (key !== undefined) sponsors.set(ring.eid[s], strategicFactionOf(key))
      } else if (type === REV.rebootEnd) {
        const relay = ring.eid[s]
        const sponsor = sponsors.get(relay)
        sponsors.delete(relay)
        const r = reach.galaxy.relays[relay] as { readonly id: string } | undefined
        if (ring.other[s] === 0 && sponsor !== undefined && r !== undefined) send({ type: 'relinkSponsored', relayId: r.id, factionId: sponsor })
      }
    }
    cursor = head
  }

  function reapplyStory(view?: Pick<StoryView, 'onlineDay'>): void {
    const access = deps.access()
    if (access === null) return
    const career = access.career()
    if (view !== undefined) storySync.alignOnlineDays(view.onlineDay, career)
    storySync.reset()
    sentTier = careerStoryTier(career)
    if (!wantsStoryReapply(career)) {
      send({ type: 'setStoryTier', tier: sentTier })
      return
    }
    sentStory = career.story
    for (const cmd of storySync.commands(career, deps.storyWorld?.())) send(cmd)
  }

  return {
    install(): void {
      bindExtraOffers(offers)
      installed = true
    },
    step(): void {
      const access = deps.access()
      if (access !== null) {
        const career = access.career()
        if (career !== lastCareer) {
          lastCareer = career
          syncCareer(career)
        }
      }
      if (deps.reach !== undefined) readReach(deps.reach)
    },
    applyStandingDeltas: applyDeltas,
    command(cmd: StrategicCommand): Promise<CommandResult> {
      return client.command(cmd).then((r) => {
        if (r.ok && r.standingDeltas !== undefined && r.standingDeltas.length > 0) applyDeltas(r.standingDeltas)
        return r
      })
    },
    setStoryTier(tier: number): void {
      const access = deps.access()
      if (access === null) return
      const out = access.apply('storyTier', (c) => withStoryTier(c, tier))
      if (!out.ok) logger.warn('charter career bridge: story tier not set', out.error)
    },
    setResonanceMeter(fitted: boolean): boolean {
      const access = deps.access()
      if (access === null) return false
      const out = access.apply('resonanceMeter', (c) => withResonanceMeter(c, fitted))
      if (!out.ok) logger.warn('charter career bridge: resonance meter not set', out.error)
      return out.ok
    },
    reapplyStory,
    workerReinit(view?: Pick<StoryView, 'onlineDay'>): void {
      sentStandings = null
      lastCareer = null
      reapplyStory(view)
    },
    recordGuardianKills(kills: readonly GuardianKill[]): void {
      const access = deps.access()
      if (access === null || kills.length === 0) return
      const out = access.apply('guardianKills', (c) => withGuardianKills(c, kills))
      if (!out.ok) logger.warn('charter career bridge: guardian kills not logged', out.error)
    },
    reconciliationOffer(): ReconciliationOffer | null {
      const access = deps.access()
      return access === null ? null : reconciliationOffer(data.contracts.reconciliation, access.career())
    },
    reconcile(): CmdResult<Career> {
      const access = deps.access()
      if (access === null) return fail('notReady', 'No career is running.')
      const r = acceptReconciliation(data.contracts.reconciliation, access.career())
      if (!r.ok) return r
      const out = access.apply('reconciliation', (c) => {
        const again = acceptReconciliation(data.contracts.reconciliation, c)
        return again.ok ? again.value : c
      })
      if (!out.ok) {
        logger.warn('charter career bridge: reconciliation not applied', out.error)
        return { ok: false, error: out.error }
      }
      return r
    },
    dispose(): void {
      if (installed) bindExtraOffers(null)
      installed = false
      tracked.clear()
      sponsors.clear()
    },
  }
}
